import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Autocomplete, Box, Grid, Stack, TextField, Typography } from "@mui/material";
import { ExperienceLevels, FullTimeId, JobLocationTypes } from "Components/CreateJob/CreateJobConstants";
import { IJobTitle } from "Components/CreateJob/CreateJobModel";
import { useNotification } from "Components/Notification";
import { ShTooltip } from "Components/SharedComponents/ShTooltip";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiState } from "shared/SharedModels";
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { AutocompleteLoaders, AutocompletePopoverProps, ShGreenBtn } from 'shared/SharedStyles/ShInputs';
import { DummyBlock } from 'shared/SharedStyles/ShLayouts';
import { ApiStates, DefaultAPIErrorMsg, LovNoMatchesText } from "shared/constants";
import { createOrUpdateJob, resetCreateOrUpdateJob, setIsDetailsEnabled } from "store/slices/employer/create-job/create-job-shared-slices";
import { createNewJob, getAreaInformationByZipcode, getJobHiringManagers, resetCreateNewJob, resetGetJobHiringManagers } from "store/slices/employer/create-job/job-title-slice";
import { getJobTitles, getJobsStates, resetGetJobRoles, resetGetJobsStates } from 'store/slices/meta-data/jobs-meta-slice';

export const JobTitle = () => {
    const [isValidZipcode, setIsValidZipcode] = useState<boolean>(true);
    const [isCityStateDisabled, sestIsCityStateDisabled] = useState<boolean>(true);
    const dispatch = useAppDispatch();
    const { jobId } = useParams();
    const notification = useNotification();
    const navigate = useNavigate();
    const { jobTitleInfo, isDetailsEnabled, createOrUpdateJobResponse, createOrUpdateJobStatus,
        requirementsMetaData
    } = useAppSelector((state) => state.employer.createJobShared);
    const { getJobHiringManagersResponse, getJobHiringManagersStatus, jobHiringManagers, newJobCreationStatus,
        newJobCreationResponse, newJobId, areaInformation, rePostJobId,
        getAreaInformationStatus
    } = useAppSelector((state) => state.employer.jobTitle);
    const { getJobTitlesResponse, getJobTitlesStatus, getJobStatesResponse, getJobStatesStatus, jobStates,
        jobTitles,
    } = useAppSelector(state => state.metadata.jobsMeta);
    const { employerId } = useAppSelector((state) => state.auth.auth);
    const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);
    const postalCodeRef = useRef<string | null>(null);

    const [jobInfo, setJobInfo] = useState<IJobTitle>({
        job_title_id: 0,
        name: '',
        hiring_manager_id: null,
        optional_hiring_manager_id: null,
        remote_job_type: null,
        city: '',
        postalcode: '',
        province_id: 0,
        neighbourhood: '',
        experience: '',
        positiontype_id: null,
        description: '',
        is_complete: false
    });

    // Set Job details if already exists.
    useEffect(() => {
        if (jobTitleInfo !== undefined) {
            setJobInfo({ ...jobTitleInfo });
        }
    }, [jobTitleInfo]);

    // Load states, roles and hiring managers.
    useEffect(() => {
        dispatch(getJobTitles());
    }, [dispatch]);

    useEffect(() => {
        if (jobStates?.length === 0) {
            dispatch(getJobsStates());
        }
    }, [dispatch, jobStates?.length]);

    useEffect(() => {
        if (jobHiringManagers?.length === 0 && employerId) {
            dispatch(getJobHiringManagers({ empId: employerId }));
        }
    }, [dispatch, employerId, jobHiringManagers?.length]);

    const cOrUJob = () => {
        if (isNaN(parseInt(jobId ?? ''))) {
            dispatch(createNewJob());
        } else {
            dispatch(createOrUpdateJob({ jobId: parseInt(rePostJobId ? rePostJobId : jobId ?? ''), payload: jobInfo }));
        }
    };

    // Post job payload after new job id is created.
    useEffect(() => {
        if (newJobCreationStatus === ApiStates.success && newJobId) {
            dispatch(createOrUpdateJob({ jobId: newJobId, payload: jobInfo }));
            dispatch(resetCreateNewJob());
        }
    }, [dispatch, jobInfo, newJobCreationStatus, newJobId]);

    // debouncing user input, calls API once user stops typing in zip code field
    const handleGetAreaInfo = useMemo(() =>
        debounce((zipCode) => dispatch(getAreaInformationByZipcode({ zipCode })), 1000), [dispatch]);

    // get area info
    useEffect(() => {
        if (jobInfo.postalcode.length >= 5 && jobInfo.postalcode !== postalCodeRef.current && getAreaInformationStatus !== ApiStates.pending) {
            handleGetAreaInfo(jobInfo.postalcode);
            postalCodeRef.current = jobInfo.postalcode;
        }
        if (isCityStateDisabled && (getAreaInformationStatus === ApiStates.failed || getAreaInformationStatus === ApiStates.success) && jobInfo.postalcode?.length >= 5)
            sestIsCityStateDisabled(false);
    }, [dispatch, jobInfo.postalcode, handleGetAreaInfo, getAreaInformationStatus, isCityStateDisabled]);

    // updating job inforation for city and provice_id based on area information
    useEffect(() => {
        if (areaInformation.city && areaInformation.state && getAreaInformationStatus === ApiStates.success) {
            const proviceId = jobStates.find((state) => state.abbreviation === areaInformation.state)?.id;
            if (proviceId) setJobInfo((job) => ({ ...job, city: areaInformation.city, province_id: proviceId }));
            setIsValidZipcode(true);
        } else if (getAreaInformationStatus === ApiStates.failed) {
            setIsValidZipcode(false);
        }
    }, [areaInformation, getAreaInformationStatus, jobStates, dispatch]);

    // Navigate to next page once Job is created or updated successfully.
    useEffect(() => {
        if (createOrUpdateJobStatus === 'success') {
            dispatch(setIsDetailsEnabled(true));
            notification.displayNotification({
                open: true,
                type: 'success',
                message: createOrUpdateJobResponse
            });
        }
    }, [createOrUpdateJobResponse, createOrUpdateJobStatus, dispatch, notification]);

    useEffect(() => {
        if (createOrUpdateJobStatus === 'success' && isDetailsEnabled) {
            navigate(`/employer/job/${newJobId ? newJobId : (rePostJobId ? rePostJobId : jobId ?? '')}/details`);
        }
    }, [createOrUpdateJobStatus, isDetailsEnabled, jobId, navigate, newJobId, rePostJobId]);

    const showFailureAlerts = (apiState: ApiState, msg: string) => {
        switch (apiState) {
            case 'failed':
                return (<>
                    <Box marginBottom={2} width='100%'>
                        <ShAlert severity="error">{msg ?? DefaultAPIErrorMsg}</ShAlert>
                    </Box>
                </>);
            default:
                break;
        }
    };

    // Disable next button.
    useEffect(() => {
        const { job_title_id, name, hiring_manager_id, remote_job_type,
            city, postalcode, province_id, experience, positiontype_id } = jobInfo;
        // If remote_job_type is 'onsite' then state,city and zip code are required.
        setIsNextDisabled(!name || (remote_job_type === 'Onsite' && (!city || !postalcode)) ||
            !experience || job_title_id === 0 || job_title_id === null ||
            // If remote_job_type is not 'remote' then state is not required.
            (remote_job_type !== 'Fully Remote' && (province_id === 0 || province_id === null)) ||
            hiring_manager_id === 0 || hiring_manager_id === null ||
            positiontype_id === 0 || positiontype_id === null || !remote_job_type ||
            (jobInfo.positiontype_id !== FullTimeId && !jobInfo.hours_per_week));
    }, [jobInfo]);

    // Reset all states on unmount.
    useEffect(() => {
        return () => {
            dispatch(resetGetJobRoles());
            dispatch(resetGetJobsStates());
            dispatch(resetGetJobHiringManagers());
            dispatch(resetCreateOrUpdateJob());
        }
    }, [dispatch]);


    return (<>
        <Stack className='elements-stack' rowGap={{ xs: 2, sm: 3, md: 3, lg: 3 }} minHeight='350px'>
            {/* API failed status */}
            {showFailureAlerts(createOrUpdateJobStatus, createOrUpdateJobResponse)}
            {showFailureAlerts(getJobTitlesStatus, getJobTitlesResponse)}
            {showFailureAlerts(getJobStatesStatus, getJobStatesResponse)}
            {showFailureAlerts(getJobHiringManagersStatus, getJobHiringManagersResponse)}
            {showFailureAlerts(newJobCreationStatus, newJobCreationResponse)}
            <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 3 }}>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        {/* Using MUI Autocomplete component to display dropdowns with inbuilt search functionality*/}
                        <Autocomplete noOptionsText={LovNoMatchesText} ListboxProps={AutocompletePopoverProps}
                            size='small' disablePortal id='role' options={jobTitles}
                            getOptionLabel={(option) => option.title || ''} fullWidth
                            groupBy={(option) => option.industry}
                            value={jobTitles?.find(jr => jr.id === jobInfo.job_title_id) ?? null}
                            renderInput={(params) => <TextField required {...params} label='Role'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (<>{getJobTitlesStatus === 'pending' ?
                                        <AutocompleteLoaders color='inherit' size={20} /> : getJobTitlesStatus === 'failed' ? <>
                                            {getJobTitlesResponse}
                                        </> : null}
                                        {params.InputProps.endAdornment}</>)
                                }} />}
                            onChange={(e, newValue) => { setJobInfo({ ...jobInfo, job_title_id: newValue?.id || 0 }) }} />
                        <ShTooltip
                            title="Choose a job role from the list of common jobs in your industry. Don't see what you're looking for? Pick the closest match.
                             You can also enter your own job title in the field below. Tip: simple and common titles work best, so don't get too creative." placement="right">
                            <HelpOutlineIcon fontSize="small" color='disabled' /></ShTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <TextField label="Job Title" variant="outlined" fullWidth size='small' value={jobInfo.name ?? ''}
                            onChange={(e) => setJobInfo({ ...jobInfo, name: e.target.value })} required />
                        <DummyBlock>
                            <HelpOutlineIcon fontSize="small" />
                        </DummyBlock>
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {/* hiring manager */}
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <Autocomplete noOptionsText={LovNoMatchesText} ListboxProps={AutocompletePopoverProps} size='small' disablePortal id='h_m' options={jobHiringManagers}
                            value={jobHiringManagers?.find(jr => jr.id === jobInfo.hiring_manager_id) ?? null}
                            getOptionDisabled={(option) => jobInfo.optional_hiring_manager_id === option.id}
                            getOptionLabel={(option) => (option.first_name + ' ' + option.last_name) || ''} fullWidth
                            renderInput={(params) => <TextField required {...params} label='Hiring Manager'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (<>{getJobHiringManagersStatus === 'pending' ?
                                        <AutocompleteLoaders color='inherit' size={20} /> : getJobHiringManagersStatus === 'failed' ? <>
                                            {getJobHiringManagersResponse}
                                        </> : null}
                                        {params.InputProps.endAdornment}</>)
                                }} />}
                            onChange={(e, newValue) => { setJobInfo({ ...jobInfo, hiring_manager_id: newValue?.id || 0 }) }} />
                        <ShTooltip
                            title="This is the person hiring for this role. They will receive notifications of new applicants." placement="right"><HelpOutlineIcon fontSize="small" color='disabled' /></ShTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {/* additional hiring manager */}
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <Autocomplete noOptionsText={LovNoMatchesText} ListboxProps={AutocompletePopoverProps} size='small' disablePortal id='a_h_m' options={jobHiringManagers}
                            value={jobHiringManagers?.find(jr => jr.id === jobInfo.optional_hiring_manager_id) ?? null}
                            getOptionDisabled={(option) => jobInfo.hiring_manager_id === option.id}
                            getOptionLabel={(option) => (option.first_name + ' ' + option.last_name) || ''} fullWidth
                            renderInput={(params) => <TextField {...params} label='Additional Hiring Manager'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (<>{getJobHiringManagersStatus === 'pending' ?
                                        <AutocompleteLoaders color='inherit' size={20} /> : getJobHiringManagersStatus === 'failed' ? <>
                                            {getJobHiringManagersResponse}
                                        </> : null}
                                        {params.InputProps.endAdornment}</>)
                                }} />}
                            onChange={(e, newValue) => { setJobInfo({ ...jobInfo, optional_hiring_manager_id: newValue?.id || null }) }} />
                        <ShTooltip placement="right"
                            title="This is the person hiring for this role. They will receive notifications of new applicants." ><HelpOutlineIcon fontSize="small" color='disabled' /></ShTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {/* job location */}
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <Autocomplete noOptionsText={LovNoMatchesText} size='small' disablePortal id='j_t' options={JobLocationTypes}
                            value={JobLocationTypes?.find(r => r.value === jobInfo.remote_job_type) ?? null}
                            getOptionLabel={(option) => option.label || ''} fullWidth
                            renderInput={(params) => <TextField required {...params} label='Job Location'
                                InputProps={{ ...params.InputProps }} />}
                            onChange={(e, newValue) => { setJobInfo({ ...jobInfo, remote_job_type: newValue?.value || null }) }} />
                        <ShTooltip placement="right"
                            title="Specify where the applicant would be working - not necessarily the headquarters." ><HelpOutlineIcon fontSize="small" color='disabled' /></ShTooltip>
                    </Stack>
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {/* ZipCode */}
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <TextField label="Zip Code" id="zip_code" variant="outlined" fullWidth size='small' value={jobInfo.postalcode ?? ''}
                            onChange={(e) => {
                                const postalcode = e.target.value.replace(/[^0-9]/g, '');
                                setJobInfo({ ...jobInfo, postalcode });
                            }}
                            required={jobInfo.remote_job_type === 'Onsite'}
                            helperText={!isValidZipcode
                                ? <Typography color={(theme) => theme.palette.warning.main} variant="caption">Zip code not found</Typography>
                                : ""}
                            inputProps={{ maxLength: 5 }} />
                        <DummyBlock>
                            <HelpOutlineIcon fontSize="small" />
                        </DummyBlock>
                    </Stack>
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <TextField label="City" variant="outlined" fullWidth size='small' value={jobInfo.city}
                            onChange={(e) => setJobInfo({ ...jobInfo, city: e.target.value })} required={jobInfo.remote_job_type === 'Onsite'}
                            disabled={isCityStateDisabled} />
                        <DummyBlock>
                            <HelpOutlineIcon fontSize="small" />
                        </DummyBlock>
                    </Stack>
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <Autocomplete noOptionsText={LovNoMatchesText} ListboxProps={AutocompletePopoverProps} size='small' disablePortal id='state' options={jobStates}
                            value={jobStates?.find(jS => jS.id === jobInfo.province_id) ?? null}
                            getOptionLabel={(option) => option.name_en || ''} fullWidth
                            renderInput={(params) => <TextField required={jobInfo.remote_job_type !== 'Fully Remote'} {...params} label='State'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (<>{(getJobStatesStatus === 'pending' || getAreaInformationStatus === 'pending') ?
                                        <AutocompleteLoaders color='inherit' size={20} /> :
                                        getJobStatesStatus === 'failed' ? <>{getJobStatesResponse}</> : null}
                                        {params.InputProps.endAdornment}</>)
                                }} />}
                            onChange={(e, newValue) => setJobInfo({ ...jobInfo, province_id: newValue?.id || 0 })}
                            disabled={isCityStateDisabled} />
                        <DummyBlock>
                            <HelpOutlineIcon fontSize="small" />
                        </DummyBlock>
                    </Stack>
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <TextField label="Neighborhood" id="neighborhood" variant="outlined" fullWidth size='small' value={jobInfo.neighbourhood ?? ''}
                            onChange={(e) => setJobInfo({ ...jobInfo, neighbourhood: e.target.value })} />
                        <ShTooltip placement="right" title="You may be hiring in a specific area of town. Make sure to mention where that is." >
                            <HelpOutlineIcon fontSize="small" color='disabled' />
                        </ShTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {/* Employment type */}
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <Autocomplete noOptionsText={LovNoMatchesText} size='small' disablePortal id='e_s' options={requirementsMetaData?.positionTypes ?? []}
                            value={requirementsMetaData?.positionTypes?.find(pT => pT.id === jobInfo.positiontype_id) ?? null}
                            getOptionLabel={(option) => option.name || ''} fullWidth
                            renderInput={(params) => <TextField required {...params} label='Employment type'
                                InputProps={{ ...params.InputProps }} />}
                            onChange={(e, newValue) => { setJobInfo({ ...jobInfo, positiontype_id: newValue?.id || 0 }) }} />
                        <ShTooltip placement="right" title="Most applicants have a preference for the type of work they're looking for and their schedules.
                         In addition, this sets expectations about compensation for the role. Sorry, but we do not accept work from home, business opportunities, or multi level marketing opportunities." >
                            <HelpOutlineIcon fontSize="small" color='disabled' />
                        </ShTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {/* Experience Level */}
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        <Autocomplete noOptionsText={LovNoMatchesText} size='small' disablePortal id='e_l' options={ExperienceLevels ?? []}
                            value={ExperienceLevels?.find(el => el.value === jobInfo.experience) ?? null}
                            getOptionLabel={(option) => option.label || ''} fullWidth
                            renderInput={(params) => <TextField required {...params} label='Experience Level'
                                InputProps={{ ...params.InputProps }} />}
                            onChange={(e, newValue) => { setJobInfo({ ...jobInfo, experience: newValue?.value || '' }) }} />
                        <ShTooltip placement="right" title={`Let applicants know what level of experience you're looking for.
                            "Entry Level" is less than one year."Experienced" is more than one year.`}>
                            <HelpOutlineIcon fontSize="small" color='disabled' />
                        </ShTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {jobInfo.positiontype_id && jobInfo.positiontype_id !== FullTimeId &&
                        <TextField type="number" label="Hours per week" id="hours_per_week" variant="outlined" fullWidth size='small' value={jobInfo.hours_per_week ?? ''}
                            onChange={(e) => setJobInfo({ ...jobInfo, hours_per_week: parseInt(e.target.value) })} required />}

                </Grid>
            </Grid>
            <ShGreenBtn variant='contained' disableElevation onClick={cOrUJob}
                disabled={isNextDisabled || createOrUpdateJobStatus === 'pending'}>
                {createOrUpdateJobStatus === 'pending' ? 'Saving...' : 'Next'}
            </ShGreenBtn>
        </Stack>
    </>)
};